import { useQuery } from "@tanstack/react-query";
import { useRealtime } from "@tcg/realtime/react";
import { Badge } from "@tcg/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCan, usePos, useRegister } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import { call } from "@/lib/api";
import { errorText } from "@/lib/errors";
import { POS_TOPICS } from "@/lib/sse";

import { jstToday } from "./IdentityCheckModal";
import { maskName } from "./SellerPanel";

export const buylistListKey = (shiftId: string | null) =>
  ["pos-buylist", "list", shiftId ?? "none"] as const;

function sellerName(seller: Record<string, unknown>): string | null {
  const name = seller.name ?? seller.fullName ?? seller.displayName;
  return typeof name === "string" ? name : null;
}

/**
 * 本日の買取 (T07-31): this shift's purchases, newest first — seller names masked (「山＊＊」), the
 * total, how they were paid, the status; updates itself from the register's stream
 * (`buylist.changed`). A manager (`kobutsu.read`) also sees today's 古物台帳 rows
 * (`GET /pos/kobutsu-ledger`, consignment-service).
 */
export function IntakeHistory() {
  const { t, i18n } = useTranslation();
  const { api, consignment } = usePos();
  const register = useRegister();
  const canLedger = useCan("kobutsu.read");
  const [tab, setTab] = useState<"purchases" | "ledger">("purchases");
  const shiftId = register.data?.openShift?.shiftId ?? null;
  const list = useQuery({
    queryKey: buylistListKey(shiftId),
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/buylist-intakes", {
          params: { query: { ...(shiftId ? { shiftId } : {}), limit: 100 } },
        }),
      ),
  });
  useRealtime([POS_TOPICS.buylist], () => void list.refetch(), {
    onResync: () => void list.refetch(),
  });
  const today = jstToday();
  const ledger = useQuery({
    queryKey: ["pos-kobutsu", "list", today],
    queryFn: () =>
      call(() =>
        consignment.GET("/v1/pos/kobutsu-ledger", {
          params: { query: { from: today, to: today, limit: 100 } },
        }),
      ),
    enabled: canLedger && tab === "ledger",
  });
  const time = (iso: string | null | undefined) =>
    iso
      ? new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "ja", {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Asia/Tokyo",
        }).format(new Date(iso))
      : "—";

  return (
    <section className="flex flex-col gap-3">
      <div role="tablist" className="flex gap-2">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "purchases"}
          onClick={() => setTab("purchases")}
          className="min-h-pos-touch rounded-md border px-5 text-lg aria-selected:border-primary aria-selected:bg-primary aria-selected:text-primary-foreground"
        >
          {t("buylist.history_title")}
        </button>
        {canLedger ? (
          <button
            type="button"
            role="tab"
            aria-selected={tab === "ledger"}
            onClick={() => setTab("ledger")}
            className="min-h-pos-touch rounded-md border px-5 text-lg aria-selected:border-primary aria-selected:bg-primary aria-selected:text-primary-foreground"
          >
            {t("buylist.ledger_title")}
          </button>
        ) : null}
      </div>
      {tab === "purchases" ? (
        list.error ? (
          <p role="alert">{errorText(list.error, t)}</p>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b text-sm text-muted-foreground">
                <th className="py-2">{t("buylist.col_time")}</th>
                <th>{t("buylist.col_ref")}</th>
                <th>{t("buylist.col_seller")}</th>
                <th>{t("buylist.col_cards")}</th>
                <th>{t("buylist.col_payout")}</th>
                <th className="text-right">{t("buylist.col_total")}</th>
                <th>{t("buylist.col_status")}</th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.data ?? []).map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="py-2">{time(row.acceptedAt ?? row.createdAt)}</td>
                  <td>{row.reference}</td>
                  <td>{maskName(sellerName(row.seller))}</td>
                  <td>{row.lines.length}</td>
                  <td>{row.payout.method ? t(`buylist.method_${row.payout.method}`) : "—"}</td>
                  <td className="text-right">
                    <MoneyText amount={row.totalOffer} />
                  </td>
                  <td>
                    <Badge
                      variant={
                        row.status === "paid" || row.status === "accepted" ? "default" : "outline"
                      }
                    >
                      {t(`buylist.status_${row.status}`)}
                    </Badge>
                  </td>
                </tr>
              ))}
              {list.data && list.data.data.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-4 text-muted-foreground">
                    {t("buylist.history_empty")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )
      ) : ledger.error ? (
        <p role="alert">{errorText(ledger.error, t)}</p>
      ) : (
        <table className="w-full text-left">
          <thead>
            <tr className="border-b text-sm text-muted-foreground">
              <th className="py-2">{t("buylist.col_no")}</th>
              <th>{t("buylist.col_time")}</th>
              <th>{t("buylist.col_item")}</th>
              <th>{t("buylist.col_seller")}</th>
              <th>{t("buylist.col_check")}</th>
              <th className="text-right">{t("buylist.col_total")}</th>
            </tr>
          </thead>
          <tbody>
            {(ledger.data?.data ?? []).map((row) => (
              <tr key={row.id} className="border-b">
                <td className="py-2">
                  {row.seqYear}-{row.seqNo}
                </td>
                <td>{time(row.entryAt)}</td>
                <td>
                  {row.itemDescription}
                  {row.minorFlag ? <Badge className="ml-1">{t("buylist.minor_flag")}</Badge> : null}
                </td>
                <td>{row.counterpartyName}</td>
                <td>{row.confirmationMethod ?? "—"}</td>
                <td className="text-right">
                  <MoneyText amount={Number.parseInt(row.price.amount, 10)} />
                </td>
              </tr>
            ))}
            {ledger.data && ledger.data.data.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-4 text-muted-foreground">
                  {t("buylist.ledger_empty")}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}
    </section>
  );
}
